import { AuthClient } from '@appcues/auth';
import normalizeUrl from 'normalize-url';

type RequestParams = Partial<Request> & {
  account?: boolean;
  responseType?: 'json' | 'text' | 'blob';
  contentType?: string | null;
  form?: FormData;
};

/**
 * throw http error
 * taken from https://github.com/sindresorhus/ky
 */
class HTTPError extends Error {
  name: string;

  response: any;

  constructor(response: any, message = '') {
    super(message || response.statusText);
    this.name = 'HTTPError';
    this.response = response;
  }
}

/**
 * Create an authenticated fetch API client for a resource
 */
export default function createFetch(url: string, auth?: AuthClient) {
  /**
   * Perform an authenticated fetch for the provided base URL
   */
  return async function createFetchMethod(
    path: string,
    { body, form, responseType, account = true, ...options }: RequestParams = {}
  ) {
    const accountId = auth ? await auth?.getAccountId() : null;
    const jwt = auth ? await auth?.getToken() : null;

    // If the request is account scoped, then automatically append the account root
    // to the request URL. Otherwise only use the base URL and provided path
    const base = account ? `${url}/accounts/${accountId}` : url;

    // Normalize URL
    const normalized = normalizeUrl(`${base}/${path ?? ''}`);

    // Make authenticated HTTP request
    const response = await fetch(normalized, {
      ...options,

      // NOTE: Do not stringify the body if FormData is used
      ...(form && { body: form }),
      ...(body && { body: JSON.stringify(body) }),

      /**
       * Append the JSON content type and authorization headers
       * NOTE: When form data is used, omit content type to ensure the browser
       *       properly handles adding the header boundary value
       */
      headers: {
        ...(!form && { 'Content-Type': 'application/json' }),
        ...(options.headers as Request['headers']),
        ...(jwt && { Authorization: `Bearer ${jwt}` }),
      },
    });

    if (!response.ok) {
      const data = await response.json();
      throw new HTTPError({ ...response, data }, data.message);
    }

    switch (responseType) {
      case 'text':
        return response.text();
      case 'blob':
        return response.blob();
      case 'json':
      default:
        return response.json();
    }
  };
}
